/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { CurrentUser } from "../features/session/session-types"

export enum EventBusEvent {
    EnabledAppsChanged = "enabledAppsChanged",
    CurrentUserLoaded = "currentUserLoaded"
}

export type EventBusPayloads = {
    [EventBusEvent.EnabledAppsChanged]: number[]
    [EventBusEvent.CurrentUserLoaded]: CurrentUser
}

type Listener<K extends keyof EventBusPayloads> = (payload: EventBusPayloads[K]) => void
type AnyListener = (payload: unknown) => void

export class EventBus {
    private listeners = new Map<keyof EventBusPayloads, Set<AnyListener>>()

    on<K extends keyof EventBusPayloads>(event: K, listener: Listener<K>): void {
        let eventListeners = this.listeners.get(event)
        if (!eventListeners) {
            eventListeners = new Set()
            this.listeners.set(event, eventListeners)
        }
        eventListeners.add(listener as AnyListener)
    }

    off<K extends keyof EventBusPayloads>(event: K, listener: Listener<K>): void {
        this.listeners.get(event)?.delete(listener as AnyListener)
    }

    emit<K extends keyof EventBusPayloads>(event: K, payload: EventBusPayloads[K]): void {
        const eventListeners = this.listeners.get(event)
        if (eventListeners) {
            for (const listener of eventListeners) {
                ;(listener as Listener<K>)(payload)
            }
        }
    }
}
